/**
 * retl-schema.ts — Reverse ETL's internal bookkeeping schema: its name per engine, and a read-only
 * probe that tells a customer their account cannot write to it BEFORE their first activation fails.
 *
 * ## Why this module exists (ACT2-737)
 *
 * A Lookout warehouse account is provisioned read-only on the customer's data. Reverse ETL, which
 * runs every activation, additionally needs to CREATE and DROP tables in its own internal schema:
 * that is where `sync_data_*`, `snapshot_*`, `unload_*`, `failed_keys_*`, `rej_*` and `sync_log`
 * live. Nothing in the setup SQL we hand out used to grant that, so the account authenticated,
 * `list_tables` worked, audiences saved — and the first activation failed on permissions, in
 * rudder-sources, far from the page that caused it.
 *
 * The name is per engine and is NOT ours to choose: `client.Client.GetRudderSchema()` in
 * rudder-sources (`integrations/sqlsource/client/client.go`) is the production authority.
 *
 * It is also overridable per account via `credentials.rudderSchema`, which rudder-sources reads
 * FIRST on every engine — and **Lookout cannot see that value today**. It belongs to the customer's
 * RudderStack rETL account, not to the Lookout connection this probe runs on, and nothing fetches
 * it. `retlInternalSchema` therefore takes an `override` that no production caller passes: it is the
 * seam for the day the account's config is in reach, not a case that is handled. Until then, an
 * rETL source configured with a non-default schema name writes a schema this probe never looks at,
 * and the probe would report its own default absent. That is why `retlPrivilegeWarning` names what
 * it checked instead of asserting that activation will fail.
 *
 * ## The probe is read-only, and fails soft
 *
 * rudder-sources verifies the boundary by creating and dropping a probe table. Lookout's driver
 * layer deliberately exposes only `executeReadOnly` and has no write verb, so we verify the
 * *privilege* through catalog reads instead of exercising it.
 *
 * Every verdict other than `missing` is silent in the UI. That asymmetry is the point: a probe that
 * cannot see an inherited grant must not tell a correctly-configured customer they are broken, so
 * anything short of definite evidence of absence reports `unknown` and says nothing. What each
 * engine can prove definitely differs, and is recorded per branch below.
 */

/** Canonical warehouse type keys, as `WarehouseTypeKey` spells them (driver strings map on the way in). */
export type RetlSchemaWarehouseType = 'POSTGRES' | 'RS' | 'SNOWFLAKE' | 'BQ' | 'DATABRICKS' | 'CLICKHOUSE';

/**
 * The internal schema per engine, mirroring rudder-sources' `GetRudderSchema()`.
 *
 * `null` means the engine is not a Reverse ETL source at all, so no internal schema exists and no
 * grant is owed. ClickHouse uses a working database rather than a schema.
 *
 * BigQuery's `rudderstack_` is a **dataset**, created and granted through IAM rather than `GRANT`
 * (`integrations/sqlsource/client/bigquery/bigquery.go:24`). Snowflake's spelling is upper because
 * unquoted identifiers fold up there.
 */
export const RETL_INTERNAL_SCHEMA: Record<RetlSchemaWarehouseType, string | null> = {
  POSTGRES: '_rudderstack',
  RS: '_rudderstack',
  SNOWFLAKE: '_RUDDERSTACK',
  BQ: 'rudderstack_',
  DATABRICKS: '_rudderstack',
  CLICKHOUSE: '_rudderstack',
};

/** Driver-string spellings (`BIGQUERY`) that the canonical keys above do not use verbatim. */
const DRIVER_TYPE_ALIASES: Record<string, RetlSchemaWarehouseType> = {
  BIGQUERY: 'BQ',
  REDSHIFT: 'RS',
  RS_IAM: 'RS',
  POSTGRESQL: 'POSTGRES',
};

/** Resolve any of our type spellings to a canonical key, or null when it is not a warehouse type. */
export function canonicalRetlWarehouseType(type: string): RetlSchemaWarehouseType | null {
  const upper = type.trim().toUpperCase();
  if (upper in RETL_INTERNAL_SCHEMA) return upper as RetlSchemaWarehouseType;
  return DRIVER_TYPE_ALIASES[upper] ?? null;
}

/**
 * The internal schema name for a warehouse type, honouring a per-account `credentials.rudderSchema`
 * override when the caller holds one. Returns null for an engine that has no internal schema, and
 * for a type we do not recognise (callers then report nothing rather than guessing a name).
 */
export function retlInternalSchema(type: string, override?: string | null): string | null {
  const canonical = canonicalRetlWarehouseType(type);
  if (canonical === null) return null;
  const trimmedOverride = override?.trim();
  if (trimmedOverride) return trimmedOverride;
  return RETL_INTERNAL_SCHEMA[canonical];
}

/**
 * - `writable` — the account holds what activation needs. Nothing to say.
 * - `missing` — definite evidence of absence. This is the only status the UI warns on.
 * - `unknown` — the probe could not decide (it errored, or the engine cannot prove the positive).
 * - `not_applicable` — the engine is not an rETL source, so no grant is owed.
 */
export type RetlPrivilegeStatus = 'writable' | 'missing' | 'unknown' | 'not_applicable';

export interface RetlPrivilegeVerdict {
  /** The schema the probe looked for, or null when none is owed / the type is unknown. */
  schema: string | null;
  status: RetlPrivilegeStatus;
  /** Operator-facing detail. Never rendered as the customer-facing copy; that is `retlPrivilegeWarning`. */
  detail?: string;
  /**
   * Set when the probe ERRORED or timed out, as opposed to answering inconclusively. Both land on
   * `unknown` and both are silent for the customer, but they mean opposite things to us: an engine
   * that cannot prove the positive is healthy and expected (Snowflake, BigQuery, Databricks), while
   * a probe that throws on every call is our bug. Without this flag a status stream could not tell
   * the two apart, and a whole engine's probe could be dead in production and look normal.
   */
  probeFailed?: true;
}

/** A row as `executeReadOnly` returns it — every driver yields plain objects keyed by column name. */
type ProbeRow = Record<string, unknown>;

/**
 * Read a probe column as a boolean-ish. Engines disagree about what they return for a count or a
 * predicate: Postgres yields a real boolean, Snowflake a NUMBER, BigQuery an INT64, and the `pg`
 * numeric parser is only globally patched inside the app's own data source, so a driver can hand
 * back a string here. `null`/`undefined` means the engine could not answer, which is not a `false`.
 */
function readFlag(row: ProbeRow, column: string): boolean | null {
  const raw = row[column] ?? row[column.toUpperCase()] ?? row[column.toLowerCase()];
  if (raw === null || raw === undefined) return null;
  if (typeof raw === 'boolean') return raw;
  if (typeof raw === 'number') return raw > 0;
  if (typeof raw === 'bigint') return raw > 0n;
  if (typeof raw === 'string') {
    const trimmed = raw.trim().toLowerCase();
    if (trimmed === 't' || trimmed === 'true') return true;
    if (trimmed === 'f' || trimmed === 'false') return false;
    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed > 0 : null;
  }
  return null;
}

/**
 * The probe SQL for a type, or null when there is nothing to probe.
 *
 * No statement emits a database name of its own: `pg_namespace` and Snowflake's `INFORMATION_SCHEMA`
 * resolve against the session's own catalog. That is the same rule the warehouse probe ladder
 * follows, and for the same reason — the folded account-key catalog would compile as a quoted
 * lower-case identifier against a real upper-case database and fail. BigQuery is the one exception
 * and carries the DRIVER's region sentinel rather than a name of ours; see that branch.
 *
 * Each statement also selects fixed column aliases (`schema_exists`, `can_write`) so `interpret` does
 * not have to know which engine produced the row.
 */
export function retlPrivilegeProbeSql(type: string, schema: string): string | null {
  const canonical = canonicalRetlWarehouseType(type);
  if (canonical === null) return null;
  // Every engine but Postgres (standard_conforming_strings) reads a backslash in a string literal as an
  // escape, so a schema name ending in `\` would swallow the closing quote there. The quote stays
  // doubled, as before: `\'` would make the read-only driver guard keep the literal body visible to its
  // keyword checks (sql-safety.ts). An apostrophe in a schema name still breaks this probe on BigQuery
  // (syntax error) and on Databricks before Spark 4.1 (reads another name). Known gap, ACT2-1203.
  const escapedSchema = canonical === 'POSTGRES' ? schema : schema.replace(/\\/g, '\\\\');
  const literal = `'${escapedSchema.replace(/'/g, "''")}'`;

  switch (canonical) {
    case 'CLICKHOUSE':
      return `SELECT count(*) AS schema_exists FROM system.databases WHERE name = ${literal}`;

    case 'POSTGRES':
    case 'RS':
      // `has_schema_privilege` is definitive here, and needs no privilege of its own — but it RAISES
      // on a schema that does not exist, which would turn a missing schema into an opaque error. The
      // oid-accepting overload avoids that: with no matching row the subquery is NULL, and the
      // function is strict, so it returns NULL rather than raising. A NULL reads as `unknown`, not
      // as `false`, so the two causes stay distinguishable.
      //
      // Redshift inherits both the catalog and the function from Postgres, but its support for the
      // oid overload is not documented; if it rejects the statement the probe fails soft to
      // `unknown` and warns nobody. Verified on a real cluster per docs/testing-plan.md.
      return `SELECT
  EXISTS (SELECT 1 FROM pg_namespace WHERE nspname = ${literal}) AS schema_exists,
  has_schema_privilege(
    current_user,
    (SELECT oid FROM pg_namespace WHERE nspname = ${literal}),
    'CREATE'
  ) AS can_write`;

    case 'SNOWFLAKE':
      // Existence only, and the write half is deliberately NOT asked for — measured, not assumed.
      //
      // An earlier revision also read `INFORMATION_SCHEMA.OBJECT_PRIVILEGES` for
      // `PRIVILEGE_TYPE IN ('CREATE TABLE','OWNERSHIP')`. It works: against a real account
      // (2026-09-11) it returned `CAN_WRITE = 1` for a correctly-granted role, so schema-level
      // grants do appear there. It also cost **6.7s**, against 2.2s for the existence read and a
      // ~5s dial the probe pays on every call because Snowflake is unpooled — which put the whole
      // probe past its 10s ceiling and made every Snowflake connection test log a timeout.
      //
      // It bought nothing a customer could see. A `can_write` hit only upgrades the verdict from
      // `unknown` to `writable`, and both render exactly nothing; a miss was never allowed to say
      // `missing`, because a grant inherited through a role hierarchy may not appear there. So the
      // expensive half could only ever change a silent verdict into a different silent verdict.
      //
      // What remains is the half that can actually warn someone: a role with no USAGE cannot see
      // the schema in SCHEMATA at all, so zero rows is definite evidence — and that is exactly the
      // account the old setup script produced, since it never created `_RUDDERSTACK`.
      return `SELECT
  COUNT(*) AS schema_exists,
  NULL AS can_write
FROM INFORMATION_SCHEMA.SCHEMATA
WHERE SCHEMA_NAME = ${literal}`;

    case 'BQ':
      // Dataset existence is all SQL can see: BigQuery grants write through IAM, which
      // INFORMATION_SCHEMA does not expose to a viewer-only service account. A missing dataset is
      // still a definite failure, because rudder-sources requires the schema to already exist and
      // never creates it (`validation.go` → `SchemaExists`). Present-but-unwritable therefore reads
      // as `unknown`, and the setup instructions carry the IAM half.
      //
      // The region qualifier is kept, and it is the one place this module is not plain SQL — but NOT
      // for the reason previously written here. **A bare `INFORMATION_SCHEMA.SCHEMATA` does work**
      // (verified live): it is `INFORMATION_SCHEMA.TABLES` and the other object-level views that
      // require a dataset or region qualifier, not `SCHEMATA`. What the bare form actually does is
      // worse than erroring — with no region it defaults to **US** and returns ZERO ROWS, so on a
      // non-US project this probe would report "no `rudderstack_` dataset" with total confidence and
      // be wrong. An error would at least fail soft and visibly; a confident zero does not.
      //
      // So the qualified form stays, now fed a region that is actually known: the account's own
      // `location` when it has one, otherwise the one `lib/bigquery-region-cache.ts` derives from
      // `getDatasets()` (ACT2-877). When neither can name a region the driver REFUSES the statement
      // by name rather than sending the sentinel or silently reading US.
      //
      // The driver's `REGION_SENTINEL` is substituted for every statement on the execute path
      // (`drivers/bigquery.ts` → `substituteRegionSentinel`), including one handed to
      // `executeReadOnly`, so the sentinel is what belongs in the text. The literal is repeated
      // rather than imported because this module is aliased into the SPA bundle and must not pull a
      // driver in; `retl-schema.test.ts` asserts the two agree.
      //
      // Scoping to the connection's own region is also the CORRECT claim, not just the available
      // one: BigQuery cannot read data in one region and write a dataset in another, so a
      // `rudderstack_` dataset outside this region could not serve this connection's syncs anyway.
      return `SELECT
  COUNT(*) AS schema_exists,
  NULL AS can_write
FROM \`region-{{REGION}}\`.INFORMATION_SCHEMA.SCHEMATA
WHERE schema_name = ${literal}`;

    case 'DATABRICKS':
      // Unity Catalog privileges live in `system.information_schema`, which a read-only principal
      // commonly cannot query, so this probes existence only and leaves write to `unknown`.
      //
      // Read through `system.` and across ALL catalogs, deliberately. A bare
      // `information_schema.schemata` resolves against the session's current catalog, and
      // `catalog` is OPTIONAL on a Databricks connection — so on a connection without one the
      // session lands in the workspace default while rETL writes somewhere else, and an existing,
      // correctly-granted `_rudderstack` would read as absent. That is a false `missing`: the one
      // verdict this module promises never to emit without proof.
      //
      // Unscoped, the two answers are asymmetric, and deliberately so. Zero rows means no catalog
      // the principal can see holds the schema, which is evidence of absence whichever catalog rETL
      // ends up using. A non-zero count is NOT evidence of presence in the right one — a stray
      // `_rudderstack` in an unrelated catalog reads as existing, so a real gap reports `unknown`
      // and says nothing. That trade only ever costs a warning; the scoped read would have invented
      // one.
      return `SELECT
  COUNT(*) AS schema_exists,
  NULL AS can_write
FROM system.information_schema.schemata
WHERE schema_name = ${literal}`;
  }
}

/** Turn probe rows into a verdict. `rows` empty means the engine answered nothing → `unknown`. */
export function interpretRetlPrivilegeRows(type: string, schema: string, rows: ProbeRow[]): RetlPrivilegeVerdict {
  const canonical = canonicalRetlWarehouseType(type);
  if (canonical === null) return { schema: null, status: 'unknown', detail: `unrecognised warehouse type ${type}` };
  if (RETL_INTERNAL_SCHEMA[canonical] === null) return { schema: null, status: 'not_applicable' };

  const row = rows[0];
  if (!row) return { schema, status: 'unknown', detail: 'probe returned no rows' };

  const exists = readFlag(row, 'schema_exists');
  if (exists === false) {
    return {
      schema,
      status: 'missing',
      detail: `${schema} is absent or invisible to this account`,
    };
  }
  if (exists === null) return { schema, status: 'unknown', detail: 'probe could not determine schema existence' };

  const canWrite = readFlag(row, 'can_write');
  if (canWrite === true) return { schema, status: 'writable' };

  // The schema exists but the write privilege did not show. Only engines whose probe is definitive
  // may call that `missing`; the rest stay silent (see the per-branch notes in the SQL above).
  const writeProbeIsDefinitive = canonical === 'POSTGRES' || canonical === 'RS';
  if (canWrite === false && writeProbeIsDefinitive) {
    return { schema, status: 'missing', detail: `no CREATE privilege on ${schema}` };
  }
  return { schema, status: 'unknown', detail: `write privilege on ${schema} not provable by a read-only probe` };
}

/**
 * The one customer-facing sentence, or null when there is nothing to say. Deliberately returns copy
 * for `missing` only — see the module header on why `unknown` is silent.
 */
export function retlPrivilegeWarning(
  verdict: RetlPrivilegeVerdict,
  options: { optionalActivationGrants?: boolean } = {},
): string | null {
  if (verdict.status !== 'missing' || !verdict.schema) return null;
  // Snowflake's existing-role activation grants are a separate, commented-out block, so re-running the setup SQL grants nothing.
  const remedy = options.optionalActivationGrants
    ? 'Copy the optional activation grants from this page, uncomment the lines your role lacks, and run them,'
    : 'Re-run the setup SQL from this page — it now grants that schema —';
  // The copy names WHICH credentials were checked on purpose. Activation does not necessarily run as
  // this connection's user: `services/audience-rudderstack.ts` matches an audience to a RudderStack
  // rETL account by host/database coordinates, never by identity, and that account may hold a
  // different database user — and may set `credentials.rudderSchema`, in which case it writes a
  // schema this probe never looked at. So state the evidence and let an admin who knows their setup
  // discount it, rather than asserting that activation will fail for a principal we did not test.
  return `Connected, but activation may fail: these credentials have no write access to ${verdict.schema}, the schema Reverse ETL writes its sync tables to. ${remedy} or ask your warehouse admin to. If Reverse ETL syncs this warehouse under a different database user or schema name, check that one instead.`;
}

/**
 * ACT2-1130: the same finding, worded for the toast after an adopt or a Connect, or null when there is
 * nothing to say. Not {@link retlPrivilegeWarning}: that copy sends the admin to the setup SQL "on this
 * page", which a toast after an adopt does not have. This one names what is missing and where to fix
 * it: the database user of the RudderStack account just chosen, in the warehouse.
 */
export function retlPrivilegeAdoptWarning(verdict: RetlPrivilegeVerdict): string | null {
  if (verdict.status !== 'missing' || !verdict.schema) return null;
  return `Reverse ETL activation may fail: this account's database user can't write to ${verdict.schema}. Grant it write access in your warehouse, or ask your warehouse admin to.`;
}

/**
 * ACT2-1130: can the account's database user see any of the customer's tables? Counted by the adopt
 * check's own `testQuery` as tables OUTSIDE `_rudderstack` (any case). `retl_schema_only`: none — the
 * user was granted what Reverse ETL needs and nothing more. `unknown`: the engine did not count
 * (BigQuery, ClickHouse, a Databricks catalog with no `information_schema`), so nothing is said.
 */
export type ReadAccessCheck = 'ok' | 'retl_schema_only' | 'unknown';

/**
 * ACT2-1130: the read-access finding, worded for the same toast as {@link retlPrivilegeAdoptWarning},
 * or null when there is nothing to say. It never blocks the adopt: the connection works, it just has
 * nothing to read yet, and a grant in the warehouse fixes it without touching Lookout.
 */
export function readAccessAdoptWarning(readAccess: ReadAccessCheck): string | null {
  if (readAccess !== 'retl_schema_only') return null;
  return "Lookout can't read your data: this account's database user can't see any tables outside the _rudderstack schema. Grant it read access to your tables in your warehouse.";
}
